import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  useColorModeValue,
  useBreakpointValue,
  Badge,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import { MdEdit, MdDelete, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import CreateCategory from "./CreateCategory";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT, GOLD_LIGHT } from "constants/brandColors";
import { getTranslatedField, translationsArrayToMap, useLocales, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";
import TranslationsCell from "components/localeNameFields/TranslationsCell";

const CATEGORIES_PAGE_ID = PAGE_IDS.CATEGORIES;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

const ListCategory = () => {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editableCategory, setEditableCategory] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const toast = useToast();
  const modalSize = useBreakpointValue({ base: "full", md: "md" });
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");
  const tableHeaderColor = useColorModeValue("gray.600", "gray.200");
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100");

  const [permissions, setPermissions] = useState({ canList: true, canEdit: true, canDelete: true, canInsert: true });

  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === CATEGORIES_PAGE_ID);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      // ignore malformed permissions blob
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(`${process.env.REACT_APP_NEWS_API_URL}/category`);
      setCategories(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to fetch categories."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleCreated = () => {
    setShowCreateModal(false);
    fetchCategories();
  };

  const handleEdit = (category) => {
    setEditErrors({});
    setEditableCategory({
      ...category,
      translations: translationsArrayToMap(category.translations, emptyNameTranslation, locales),
      slug: category.slug || "",
      color: category.color || "",
      order: category.order ?? "",
    });
  };

  const setEditTranslationField = (code, value) => {
    setEditableCategory((prev) => ({
      ...prev,
      translations: { ...prev.translations, [code]: { ...emptyNameTranslation(), ...prev.translations[code], name: value } },
    }));
  };

  const handleSave = async () => {
    const errors = validateNameTranslations(editableCategory.translations, locales, t);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSaving(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (editableCategory.translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: CATEGORIES_PAGE_ID,
        categoryId: editableCategory.id,
        translations: translationsPayload,
        slug: editableCategory.slug.trim(),
        color: editableCategory.color.trim(),
        ...(editableCategory.order !== "" && { order: Number(editableCategory.order) }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/category`,
        "PUT",
        data
      );
      if (!response) {
        throw new Error("Failed to update category");
      }
      await fetchCategories();
      setEditableCategory(null);
      toast({
        title: t("Category updated successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to update category."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/category`,
        "DELETE",
        { page_id: CATEGORIES_PAGE_ID, categoryId }
      );
      if (!response) {
        throw new Error("Failed to delete category");
      }
      setCategories((prev) => prev.filter((c) => c.id !== categoryId));
      toast({
        title: t("Category deleted successfully."),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      // Server returns 400 when a News item or SubCategory still references
      // this category — surface that message instead of a generic failure.
      toast({
        title: t("Unable to delete category."),
        description: error.message || t("It may still be in use by news or subcategories."),
        status: "error",
        duration: 6000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // Quick one-click on/off toggle right in the table, same pattern as the
  // Languages page's Switch — separate from the full edit modal so
  // activating/deactivating a category doesn't require opening a form.
  const handleToggleActive = async (category) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/category`,
        "PUT",
        { page_id: CATEGORIES_PAGE_ID, categoryId: category.id, isActive: !category.isActive }
      );
      if (!response) {
        throw new Error("Failed to update category");
      }
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, isActive: !c.isActive } : c)));
    } catch (error) {
      toast({
        title: t("An error occurred."),
        description: error.message || t("Failed to toggle category status."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const filteredCategories = categories.filter((c) => {
    const q = searchQuery.toLowerCase();
    return locales.some(({ code }) => getTranslatedField(c.translations, code).toLowerCase().includes(q));
  });

  return (
    <Card direction="column" w="100%" px="0px" overflowX={{ sm: "scroll", lg: "hidden" }} boxShadow="lg">
      <Flex px={{ base: "10px", md: "25px" }} justify="space-between" mb="20px" align="center" flexWrap="wrap">
        <Flex align="center">
          <Text color="gray.800" fontSize={{ base: "18px", md: "22px" }} fontWeight="700" lineHeight="100%" mb={{ base: "10px", md: "0" }}>
            {t("Categories")}
          </Text>
          <Button
            onClick={() => setShowCreateModal(true)}
            isDisabled={!permissions.canInsert}
            mb={1}
            ml={4}
            bg={GOLD_BORDER}
            color="#1A1A1A"
            _hover={{ bg: GOLD_TEXT, color: "white" }}
            _active={{ bg: GOLD_TEXT, color: "white" }}
            leftIcon={<MdAdd />}
            borderRadius="full"
            boxShadow="sm"
          >
            {t("Add Category")}
          </Button>
        </Flex>
        <Input
          placeholder={t("Search category")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="sm"
          w={{ base: "100%", md: "200px" }}
          borderColor={GOLD_BORDER}
          focusBorderColor={GOLD_BORDER}
        />
      </Flex>

      <Box overflowX="auto" overflowY="auto" maxH="60vh" px="0px">
        {loading ? (
          <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr bg={tableHeaderBg}>
                <Th color={tableHeaderColor} fontWeight="600">{t("Name")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Slug")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Status")}</Th>
                <Th color={tableHeaderColor} fontWeight="600">{t("Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredCategories.map((category) => (
                <Tr key={category.id} borderBottom="1px solid" borderColor="gray.100" _hover={{ bg: rowHoverBg }}>
                  <Td>
                    <TranslationsCell
                      translations={category.translations}
                      locales={locales}
                      prefix={category.color && <Box w="10px" h="10px" borderRadius="full" bg={category.color} flexShrink={0} />}
                    />
                  </Td>
                  <Td>{category.slug || "—"}</Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Badge colorScheme={category.isActive ? "green" : "gray"} variant="solid" px={2} py={1} borderRadius="md" fontSize="0.85em">
                        {category.isActive ? t("Active") : t("Inactive")}
                      </Badge>
                      <Switch
                        size="sm"
                        isChecked={category.isActive}
                        isDisabled={!permissions.canEdit}
                        onChange={() => handleToggleActive(category)}
                        colorScheme="brand"
                      />
                    </Flex>
                  </Td>
                  <Td>
                    <Flex>
                      <Button
                        variant="outline"
                        borderColor={GOLD_BORDER}
                        color={GOLD_TEXT}
                        _hover={{ bg: GOLD_LIGHT }}
                        size="sm"
                        mr="2"
                        isDisabled={!permissions.canEdit}
                        onClick={() => handleEdit(category)}
                        leftIcon={<MdEdit />}
                      >
                        {t("Edit")}
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        isDisabled={!permissions.canDelete}
                        onClick={() => handleDelete(category.id)}
                        leftIcon={<MdDelete />}
                      >
                        {t("Delete")}
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {editableCategory && (
        <Modal isOpen={true} onClose={() => setEditableCategory(null)} size={modalSize}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader borderBottom="1px solid" borderColor="gray.100">
              <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
                {t("Edit Category")}
              </Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <LocaleNameTabs
                locales={locales}
                translations={editableCategory.translations}
                errors={editErrors}
                onChange={setEditTranslationField}
                borderColor={GOLD_BORDER}
              />

              <Box mt="16px">
                <Text fontSize="sm" color="gray.600" mb="2">{t("Slug")}</Text>
                <Input
                  value={editableCategory.slug}
                  onChange={(e) => setEditableCategory({ ...editableCategory, slug: e.target.value })}
                  {...fieldProps}
                />
              </Box>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Color")}</Text>
                  <Flex align="center" gap={2}>
                    <Input
                      type="color"
                      value={editableCategory.color || "#C7B45E"}
                      onChange={(e) => setEditableCategory({ ...editableCategory, color: e.target.value })}
                      w="56px"
                      p="4px"
                      {...fieldProps}
                    />
                    <Input
                      value={editableCategory.color}
                      onChange={(e) => setEditableCategory({ ...editableCategory, color: e.target.value })}
                      {...fieldProps}
                    />
                  </Flex>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.600" mb="2">{t("Order")}</Text>
                  <Input
                    type="number"
                    value={editableCategory.order}
                    onChange={(e) => setEditableCategory({ ...editableCategory, order: e.target.value })}
                    {...fieldProps}
                  />
                </Box>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
              <Button variant="outline" borderColor="gray.200" color="gray.700" onClick={() => setEditableCategory(null)}>
                {t("Cancel")}
              </Button>
              <Button
                bg={GOLD_BORDER}
                color="#1A1A1A"
                _hover={{ bg: GOLD_TEXT, color: "white" }}
                _active={{ bg: GOLD_TEXT, color: "white" }}
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size={modalSize}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.100">
            <Text color={GOLD_TEXT} fontSize="2xl" fontWeight="700">
              {t("Create Category")}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <CreateCategory onCreated={handleCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default ListCategory;
